import React, { useContext } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { useBreakpoints } from "../../lib/breakpoints";
import { BackLocationsContext } from "../../context/BackLocationsProvider";
import { APP_NAME } from "../../helpers/constants";
import { trackEvent } from "../../helpers/mixpanel";

/**
 * Logo - Clumse brand mark + logotype.
 *
 * Rendering rules (Industrial Brand System):
 *   - LogoMark: PNG mark rendered as hard-black stamp on #F8FAFC field
 *   - LogoText: Inter Heavy 900, all-caps, tracking-wide (0.08em), Slate-800
 *   - Transitions: none (mechanical, not ease)
 *   - color="white": invert filter for dark surfaces (mark only, unless fullColorMark)
 *   - fullColorMark: show /logo.png without monochrome filter (nav/footer on dark UI)
 */
const Logo = ({
  size = "default",
  linkTo = "/",
  showTagline = false,
  displayMode = "both",
  color = "black",
  /** When true, render the mark without the bordered stamp field (nav bar/footer). */
  hideIconFrame = false,
  /** When true, render the PNG mark in original colors (no stamp filter). */
  fullColorMark = false,
}) => {
  const { isMobile, isTabletOrAbove } = useBreakpoints();
  const { clearBackLocations } = useContext(BackLocationsContext);

  const handleNavigation = (path) => {
    clearBackLocations();
    trackEvent("Navigation Click", {
      destination: path,
      from_page:
        typeof window !== "undefined" ? window.location.pathname : "/",
      is_mobile: isMobile,
      is_tablet: isTabletOrAbove,
    });
  };

  const getLogoSize = () => {
    switch (size) {
      case "small":   return { width: "28px", height: "28px" };
      case "medium":  return { width: "40px", height: "40px" };
      case "large":   return { width: "56px", height: "56px" };
      default:        return { width: "34px", height: "34px" };
    }
  };

  const logoSize = getLogoSize();

  const renderLogo = () => (
    <LogoContainer>
      {(displayMode === "icon" || displayMode === "both") && (
        <LogoMarkWrap
          $white={color === "white"}
          $bare={hideIconFrame}
          style={logoSize}
        >
          <LogoIcon
            src="/logo.png"
            alt={`${APP_NAME} mark`}
            $color={color}
            $fullColor={fullColorMark}
          />
        </LogoMarkWrap>
      )}
      {(displayMode === "text" || displayMode === "both") && (
        <LogoText $size={size} $color={color}>
          {APP_NAME}
        </LogoText>
      )}
    </LogoContainer>
  );

  return (
    <LogoWrapper>
      {linkTo ? (
        <StyledLink to={linkTo} onClick={() => handleNavigation(linkTo)}>
          {renderLogo()}
        </StyledLink>
      ) : (
        renderLogo()
      )}
    </LogoWrapper>
  );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

/**
 * Mark field - Slate-50 (#F8FAFC) stamp background per brand spec.
 * On dark surfaces (color=white) the field flips to Slate-800.
 */
const LogoMarkWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.$bare ? "transparent" : props.$white ? "#1e293b" : "#F8FAFC"};
  border: ${(props) =>
    props.$bare
      ? "none"
      : `2px solid ${props.$white ? "#F8FAFC" : "#1e293b"}`};
  flex-shrink: 0;
`;

const LogoIcon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  /* Hard stamp treatment: pure black or inverted white; skip when full-color mark */
  filter: ${(props) =>
    props.$fullColor
      ? "none"
      : props.$color === "white"
        ? "brightness(0) invert(1)"
        : "brightness(0) invert(0)"};
`;

const LogoText = styled.span`
  font-family: var(--font-stack-sans);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  line-height: 1;
  color: ${(props) => (props.$color === "white" ? "#FFFFFF" : "#1e293b")};
  font-size: ${(props) => {
    switch (props.$size) {
      case "small":  return "1.05rem";
      case "medium": return "1.3rem";
      case "large":  return "1.75rem";
      default:       return "1.2rem";
    }
  }};
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
`;

export default Logo;
